import { h, Component } from 'preact'
import axios from 'axios'
import Header from './Header'
import Sidebar from './Sidebar'
import Loader from '../ui-components/Loader'
import AlertComponent from '../ui-components/AlertComponent'
import DraftDeletedModal from '../create-topic-page/DraftDeletedModal'
import Editor from '../editor'
import globals from '../../utils/globals'
import tracker from '../../components/tracker/publish'
import DraftDeleteConfirmationModal from '../create-topic-page/DraftDeleteConfirmationModal'
import DraftPublishedModal from '../create-topic-page/DraftPublishedModal'
import SchedulePublishModal from '../create-topic-page/SchedulePublishModal'
import SchedulePublishConfirmation from '../create-topic-page/SchedulePublishConfirmation'
import CancelScheduleConfirmationModal from '../create-topic-page/CancelScheduleConfirmationModal'
import DateFormatter from '../create-topic-page/DateFormatter'
import {
  Metadata,
  PublishResponse,
  ErrorResponse,
  PropsArticleCreator,
  StateArticleCreator,
  PollWithNoVotes
} from './Types'
import ArticlePreviewModal from '../create-topic-page/PreviewModal'
import {csrfToken} from '../utils/csrfToken'
import PreviewService from '../../services/previewService'
import {CONTENT_TYPE_ARTICLE} from '../utils/contentType'

const errorMessages = {
  titleEmpty: 'Please add a title',
  titleTooShort: 'Title must contain at least 3 characters',
  forumIdEmpty: 'Please select a category',
  contentEmptyOrTooShort: 'The content is empty or too short'
}

const endpoints = {
  save: '/draft/save',
  publish: '/draft/publish',
  schedule: '/draft/schedule',
  cancelSchedule: '/draft/cancelSchedule'
}

declare var global: any

class ArticleCreator extends Component<PropsArticleCreator, StateArticleCreator> {
  private csrfToken: string = ''
  private hasFileInProgress: boolean = false
  private poll: PollWithNoVotes | null = null
  private attachments: string[] | null = null
  private content: string | null = null

  constructor (props: PropsArticleCreator) {
    super()
    const article = props.article || this.getDefaultArticleState(props)

    this.poll = article.poll == null ? null : {
        question: article.poll.question,
        options: article.poll.options.map((pollOption) => (pollOption.option))
    }
    this.state = {
      ...article,
      content_type: 'article',
      isLoading: false,
      isDraft: false,
      draftSaved: false,
      triedToPublish: false,
      hasUnsavedChanges: false,
      showDraftDeletedModal: false,
      showDeletedConfirmationModal: false,
      showDraftPublishedModal: false,
      showSchedulePublishModal: false,
      showCancelScheduleConfirmationModal: false,
      showArticlePreviewModal:false,
      isDraftDeleted: false,
      previewSettingValue: false,
      errors: {
        title: '',
        forum_id: ''
      },
      alert: {
        messageType: null,
        messageText: ''
      },
      scheduledDateInputValue: {
        date: null,
        hours: null,
        minutes: null,
        period: null,
        error: ''
      }
    }

    if (this.state.meta) this.setState({
        articleId: this.state.meta.id,
        draftSaved: true,
        previewSettingValue: this.state.meta.is_preview_public
    })
    this.csrfToken = csrfToken()
    this.hasFileInProgress = false
    this.onCloseDraftDeleteModal = this.onCloseDraftDeleteModal.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.onCloseDeleteConfirmationModal = this.onCloseDeleteConfirmationModal.bind(this)
    this.onCloseDraftPublishedModal = this.onCloseDraftPublishedModal.bind(this)
    this.onEditorLoaded = this.onEditorLoaded.bind(this)
    this.onUnload = this.onUnload.bind(this)
    this.onTitleChanged = this.onTitleChanged.bind(this)
    this.onSidebarItemChanged = this.onSidebarItemChanged.bind(this)
    this.onCloseSchedulePublishModal = this.onCloseSchedulePublishModal.bind(this)
    this.onCancelSchedule = this.onCancelSchedule.bind(this)
    this.setScheduledDate = this.setScheduledDate.bind(this)
    this.onClosePreview = this.onClosePreview.bind(this)
    this.onSubmitArticlePreview = this.onSubmitArticlePreview.bind(this)
    this.onChangeArticlePreview = this.onChangeArticlePreview.bind(this)
    this.showSomethingWentWrongMessage = this.showSomethingWentWrongMessage.bind(this)
    this.showSuccessMessage = this.showSuccessMessage.bind(this)
  }

  getDefaultArticleState (props: PropsArticleCreator) {
    return {
      articleId: null,
      featured_image: null,
      forum_id: props.inGroup || null,
      open: true, // allow replies
      public_label: null,
      sticky: false,
      tags: [],
      title: '',
      poll: null,
      meta: null
    }
  }

  componentDidMount () {
    window.addEventListener('beforeunload', this.onUnload)
    window.addEventListener('editorLoaded', this.onEditorLoaded)
  }

  componentWillUnmount () {
    window.removeEventListener('beforeunload', this.onUnload)
    window.removeEventListener('editorLoaded', this.onEditorLoaded)
  }

  onUnload (e: BeforeUnloadEvent) {
    const { isDraftDeleted, hasUnsavedChanges, title: hasTitle } = this.state
    const quillEditor = document.querySelector('.ql-editor')

    const hasContent = quillEditor
      ? !quillEditor.classList.contains('ql-blank')
      : !!this.getEditorContent().content

    if (hasUnsavedChanges && (hasTitle || hasContent) && !isDraftDeleted) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  onEditorLoaded () {
    const { article } = this.props
    const { content: bbcode } = this.state
    const wysiwygEditor = global.app.editor.currentView.wysiwygEditor

    if (article) {
      const htmlContent = '<div>' + wysiwygEditor.transformDialectToHtml(bbcode) + '</div>'
      wysiwygEditor.insertHtml(htmlContent)
    }

    setTimeout(() => {
      wysiwygEditor.quill.on('text-change', () => {
        this.setHasUnsavedChanges()
      })
      window.addEventListener('attachmentsUpdated', () => {
        this.setHasUnsavedChanges()
      })
    }, 0)
  }

  getEditorContent () {
    const app = global.app
    if (!global.app.editor) {
      return {
        content: this.content,
        attachments: this.attachments,
        poll: this.poll
      }
    }
    return {
      content: app.editor.currentView.getContent(),
      attachments: app.editorModel.get('attachments'),
      poll: app.editorModel.get('poll')
    }
  }

  getData (isDraft: boolean) {
    const commonData = Object.assign(this.state, this.getEditorContent())

    if (this.state.articleId) {
      return Object.assign(commonData, { article_id: this.state.articleId })
    } else {
      return Object.assign(commonData, { isDraft: isDraft })
    }
  }

  getEndpoint (isDraft: boolean) {
    return isDraft ? endpoints.save : endpoints.publish
  }

  handlePublishResponse (res: PublishResponse) {
    const articleModerationPage = res.headers.location

    if (articleModerationPage) {
      const { content_type, title, forum_id, draftSaved } = this.state
      const { userid, role } = window.inSidedData.user

      tracker.trackEvent('userevent', {
        'content_type': content_type,
        'event_source': 'control',
        'type': 'Topic Created'
      })

      tracker.trackEvent('userevent', {
        'type': 'Article Published',
        'article_id': res.data.id,
        'article_title': title,
        'article_category': forum_id,
        'author_id': userid,
        'author_role': role,
        'published_immediately': !draftSaved
      })

      window.location.assign(articleModerationPage)
    }
  }

  handleSaveDraftResponse (res: { data: Metadata }) {
    const base = window.location.origin
    const draftUrl = base + '/draft/' + res.data.id
    window.history.pushState(null, '', draftUrl)

    if (!this.state.draftSaved) {
      tracker.trackEvent('userevent', {
        'type': 'Article Draft Created',
        'article_title': this.state.title,
        'article_category': this.state.forum_id,
        'community_id': globals('communityId'),
        'author_id': window.inSidedData.user.userid,
        'author_role': window.inSidedData.user.role
      })
    }

    this.setState({
      isLoading: false,
      draftSaved: true,
      articleId: res.data.id,
      meta: {
        id: res.data.id,
        created_at: res.data.created_at,
        created_by: res.data.created_by,
        last_edited_by: res.data.last_edited_by,
        last_edited_at: res.data.last_edited_at,
        scheduled_at: res.data.scheduled_at,
        scheduled_by: res.data.scheduled_by,
        is_scheduled: res.data.is_scheduled,
        is_preview_public: res.data.is_preview_public
      },
      alert: { messageType: null, messageText: '' }
    })
  }

  handlePostErrors (responseError: ErrorResponse) {
    this.setState({ isLoading: false })

    const errorsText: string[] = []
    const errors = responseError.data

    if (responseError) {
      if(errors) {
        Object.keys(errors).forEach((errorKey: string) => {
          if (errorKey === 'content') {
            errorsText.push(errorMessages.contentEmptyOrTooShort)
            return
          }

          errorsText.push(errors[errorKey])
        })
      }

      if (responseError.status === 404) {
        this.setState({
          isDraftDeleted: true,
          showDeletedConfirmationModal: false,
          showDraftDeletedModal: true,
          showSchedulePublishModal: false
        })
        return
      }

      if ((responseError.status === 422 && responseError.config.method === 'delete') || responseError.status === 400) {
        this.setState({
          showDeletedConfirmationModal: false,
          showDraftPublishedModal: true,
          isLoading: false
        })
        return
      }

      this.setState({
        alert: {
          messageType: 'error',
          messageText: errorsText.length > 0 ? errorsText.join('\n') : 'Something went wrong'
        },
        showSchedulePublishModal: false
      })
    }
  }

  hasErrors () {
    this.validateTitle()
    this.validateCategory()

    return this.state.errors.title !== '' || this.state.errors.forum_id !== ''
  }

  validateTitle () {
    const title = this.state.title
    if (!title) {
      this.setError('title', errorMessages.titleEmpty)
    } else if (title.length < 3) {
      this.setError('title', errorMessages.titleTooShort)
    } else {
      this.setError('title', '')
    }
  }

  validateCategory () {
    const forumId = this.state.forum_id
    if (!forumId) {
      this.setError('forum_id', errorMessages.forumIdEmpty)
    } else {
      this.setError('forum_id', '')
    }
  }

  validateScheduleDate () {
    const { hours, minutes, period } = this.state.scheduledDateInputValue
    if (!hours || !minutes || !period) {
      this.setScheduleError('Please choose a date/time.')
      return
    }

    const currentTimestamp = Date.now() / 1000
    const scheduleTimestamp = DateFormatter.getUtcTimestamp(this.state.scheduledDateInputValue as any)
    if (scheduleTimestamp <= currentTimestamp) {
      this.setScheduleError('The date and time you have selected is in the past. Please choose a date and time in the future.')
      return
    }

    this.setScheduleError('')
  }

  setError (key: string, value: string) {
    this.setState({
      errors: {
        ...this.state.errors,
        [key]: value
      }
    })
  }

  setScheduleError (message: string) {
    this.setState({
      scheduledDateInputValue: {
        ...this.state.scheduledDateInputValue,
        error: message
      }
    })
  }

  setHasUnsavedChanges () {
    if (!this.state.hasUnsavedChanges) {
      this.setState({ hasUnsavedChanges: true })
    }
  }

  onCloseDraftDeleteModal () {
    this.setState({ showDraftDeletedModal: false })
  }

  onCloseDeleteConfirmationModal () {
    this.setState({ showDeletedConfirmationModal: false })
  }

  onCloseDraftPublishedModal () {
    this.setState({ showDraftPublishedModal: false })
  }

  onClosePreview () {
      this.setState({ showArticlePreviewModal: false })
  }

  onCloseSchedulePublishModal () {
    this.setState({
      showSchedulePublishModal: false
    })
  }

  onTitleChanged (value: string) {
    const { triedToPublish } = this.state
    this.setState({ title: value })

    this.setHasUnsavedChanges()

    if (triedToPublish) {
      this.validateTitle()
    }
  }

  onSidebarItemChanged (field: string, value: string | boolean | number | undefined) {
    const { triedToPublish } = this.state
    this.setState({
      [field as any]: value
    } as Pick<StateArticleCreator, keyof StateArticleCreator>)

    this.setHasUnsavedChanges()

    if (triedToPublish && field === 'forum_id') {
      this.validateCategory()
    }
  }

  onPublish (isDraft: boolean, isSchedule: boolean) {
    this.setState({
      triedToPublish: true
    })

    if (this.hasErrors()) {
      this.setState({ showSchedulePublishModal: false })
      return
    }

    if (this.hasFileInProgress) {
      this.setState({
        alert: {
          messageType: 'error',
          messageText: 'Please wait for your files to be uploaded'
        },
        showSchedulePublishModal: false
      })

      return
    }

    this.setState({ isLoading: true, ...isDraft as {} })

    axios.defaults.headers.common = {
      'X-CSRF-TOKEN': this.csrfToken
    }
    const endpoint = this.getEndpoint(isDraft)
    const data = this.getData(isDraft)

    axios.post(endpoint, data).then(res => {
      if (res.status !== 201) {
        this.showSomethingWentWrongMessage()
        return
      }

      this.setState({
        hasUnsavedChanges: false
      })

      if (isDraft) {
        this.handleSaveDraftResponse(res)
        if (isSchedule) {
          this.onSchedule()
        }
      } else {
        this.handlePublishResponse(res)
      }
    }).catch(error => {
      const responseError = error.response || null
      this.handlePostErrors(responseError)
    })
  }

  redirectToOverview (url: string) {
    this.setState({ hasUnsavedChanges: false })
    window.location.assign(`${url}?deleted&title=${this.state.title}`)
  }

  onDelete () {
    const { articleId, title } = this.state

    this.setState({ isLoading: true })

    axios.defaults.headers.common = {
      'X-CSRF-TOKEN': this.csrfToken
    }
    axios.delete('/draft/delete', { params: { draftId: articleId, title: title }
    }).then(res => {
      this.redirectToOverview(res.data.redirect)
    }).catch(error => {
      const responseError = error.response
      this.handlePostErrors(responseError)
    })
  }

  onSchedule () {
    this.validateScheduleDate()

    const { articleId, scheduledDateInputValue } = this.state
    if (scheduledDateInputValue.error) {
      return
    }

    axios.defaults.headers.common = {
      'X-CSRF-TOKEN': this.csrfToken
    }
    axios.post(endpoints.schedule, {
      article_id: articleId,
      schedule_date: DateFormatter.getUtcTimestamp(scheduledDateInputValue as any)
    }).then(res => {
      if (res.status !== 200) {
        this.showSomethingWentWrongMessage()
        return
      }
      this.handleArticleScheduledResponse(res)
      this.trackArticleScheduledEvent()
    }).catch(error => {
      this.handlePostErrors(error.response || null)
    })
  }

  onSubmitArticlePreview() {
    const {articleId, previewSettingValue} = this.state

    if (!articleId) return

    PreviewService.setVisibility(articleId, CONTENT_TYPE_ARTICLE, previewSettingValue)
      .then(res => {
        if (res.status !== 204) {
          this.showSomethingWentWrongMessage()
          return
        }
        this.handleArticlePreviewResponse()
        this.trackPreviewChangedEvent(previewSettingValue)
      }).catch(error => {
      this.showSomethingWentWrongMessage()
    })
  }

  async onCancelSchedule () {
    try {
      axios.defaults.headers.common = {
        'X-CSRF-TOKEN': this.csrfToken
      }
      await axios.post(endpoints.cancelSchedule, {
        article_id: this.state.articleId
      })
      this.setState({
        showCancelScheduleConfirmationModal: false
      })
      this.state.meta && this.setState({
        meta: {
          ...this.state.meta,
          is_scheduled: false
        }
      })
    } catch (e) {
      this.showSomethingWentWrongMessage()
    }
  }

  showSomethingWentWrongMessage () {
    this.setState({
      isLoading: false,
      alert: { messageType: 'error', messageText: 'Something went wrong, please try again later' },
      showSchedulePublishModal: false,
      showArticlePreviewModal: false
    })
  }

  showSuccessMessage(message: string) {
      this.setState({
        alert: {
          messageType: 'success',
          messageText: message
        }
      })
  }

  handleArticleScheduledResponse (res: { data: Metadata }) {
    this.setState({
      isLoading: false,
      showSchedulePublishModal: false,
      meta: {
        id: res.data.id,
        created_at: res.data.created_at,
        created_by: res.data.created_by,
        last_edited_by: res.data.last_edited_by,
        last_edited_at: res.data.last_edited_at,
        scheduled_at: res.data.scheduled_at,
        scheduled_by: res.data.scheduled_by,
        is_scheduled: res.data.is_scheduled,
        is_preview_public: res.data.is_preview_public
      },
      alert: { messageType: null, messageText: '' }
    })
  }

  handleArticlePreviewResponse () {
    this.setState(state => ({
      isLoading: false,
      showArticlePreviewModal: false,
      meta: { ...state.meta as Metadata, ...{ is_preview_public: state.previewSettingValue }},
      alert: { messageType: null, messageText: '' }
    }))
  }

  trackArticleScheduledEvent () {
    tracker.trackEvent('userevent', {
      'type': 'Article Scheduled',
      'article_id': this.state.articleId,
      'article_title': this.state.title,
      'scheduler_id': window.inSidedData.user.userid,
      'scheduler_name': window.inSidedData.user.name,
      'scheduler_role': window.inSidedData.user.role
    })
  }

  trackPreviewChangedEvent (isPreviewSettingPublic: boolean) {
    tracker.trackEvent('userevent', {
      'type': 'Content Sharing Settings Changed',
      'content_type': CONTENT_TYPE_ARTICLE,
      'id': this.state.articleId,
      'title': this.state.title,
      'user_id': window.inSidedData.user.userid,
      'user_name': window.inSidedData.user.name,
      'user_role': window.inSidedData.user.role,
      'setting': isPreviewSettingPublic ? 'anonymous users' : 'control users'
    })
  }

  setScheduledDate (field: string, value: Date[] | string | null) {
    this.setState({
      scheduledDateInputValue: {
        ...this.state.scheduledDateInputValue,
        [field]: value
      }
    })
  }

  onChangeArticlePreview(value: boolean) {
    this.setState({
        previewSettingValue: value
    })
  }

  render (props: PropsArticleCreator, state: StateArticleCreator) {
    const {
      articleId,
      title,
      forum_id,
      open,
      sticky,
      public_label,
      featured_image,
      tags,
      isLoading,
      draftSaved,
      hasUnsavedChanges,
      meta,
      errors,
      alert,
      showDraftDeletedModal,
      showDeletedConfirmationModal,
      showDraftPublishedModal,
      showSchedulePublishModal,
      scheduledDateInputValue,
      showCancelScheduleConfirmationModal,
      showArticlePreviewModal,
      previewSettingValue
    } = state

    const {
      categories,
      phrases,
      isKnowledgeBaseEnabled,
      isGroupsEnabled,
      CKEditor,
      isPublicTagManagementEnabled,
      canEndUserAddPublicTags
    } = props

    const labels = [
      phrases['topic.public_label.suggestion.blog'],
      phrases['topic.public_label.suggestion.news'],
      phrases['topic.public_label.suggestion.faq'],
      phrases['topic.public_label.suggestion.tutorial'],
      phrases['topic.public_label.suggestion.interview']
    ]

    return (
      <div>
        <Header
          draftSaved={draftSaved}
          hasUnsavedChanges={hasUnsavedChanges}
          title={title}
          error={errors.title}
          onTitleChanged={this.onTitleChanged}
          meta={meta}
          onPublish={() => this.onPublish(false, false)}
          onSaveAsDraft={() => this.onPublish(true, false)}
          onDeleteHandler={() => this.setState({ showDeletedConfirmationModal: true })}
          onSchedulePublish={() => this.setState({ showSchedulePublishModal: true })}
        />
        {isLoading && <Loader />}
        <div className='topic-post'>
          <AlertComponent messageType={alert.messageType} message={alert.messageText} sticky />
          {meta && meta.is_scheduled && meta.scheduled_at &&  meta.scheduled_by && <SchedulePublishConfirmation
            scheduledDate={meta.scheduled_at}
            scheduledBy={meta.scheduled_by}
            onShowCancelSchedule={() => this.setState({ showCancelScheduleConfirmationModal: true })}
          />
          }
          <Editor
            isControl
            data={this.state.content}
            attachments={this.state.attachments}
            CKEditor={CKEditor || false}
            canHavePoll={articleId === null}
            onPollUpdate={(poll: PollWithNoVotes) => {
              this.poll = poll
              this.setHasUnsavedChanges()
            }}
            onChange={(content: string, attachments: string[], hasFileInProgress:boolean) => {
              this.content = content
              this.attachments = attachments
              this.hasFileInProgress = hasFileInProgress
              this.setHasUnsavedChanges()
            }}
            poll={this.poll} />
        </div>
        <Sidebar
          articleId={articleId}
          categories={categories}
          labels={labels}
          featuredImage={featured_image || ''}
          onUpdate={this.onSidebarItemChanged}
          error={errors.forum_id}
          allowReplies={open}
          sticky={sticky}
          forum_id={forum_id}
          public_label={public_label}
          tags={tags}
          isKnowledgeBaseEnabled={isKnowledgeBaseEnabled}
          isGroupsEnabled={isGroupsEnabled}
          isPreviewPublic={meta && meta.is_preview_public}
          onShowArticlePreviewModal={() => this.setState({ showArticlePreviewModal: true })}
          onError={this.showSomethingWentWrongMessage}
          onSuccess={this.showSuccessMessage}
          isPublicTagManagementEnabled={isPublicTagManagementEnabled}
          canEndUserAddPublicTags={canEndUserAddPublicTags}
        />
        <DraftDeletedModal enableModal={showDraftDeletedModal} onClose={this.onCloseDraftDeleteModal} />
        <DraftPublishedModal contentType='article' enableModal={showDraftPublishedModal} onClose={this.onCloseDraftPublishedModal} />
        <DraftDeleteConfirmationModal enableModal={showDeletedConfirmationModal} onClose={this.onCloseDeleteConfirmationModal} onDelete={this.onDelete} />
        <CancelScheduleConfirmationModal
          contentType='article'
          enableModal={showCancelScheduleConfirmationModal}
          onClose={() => this.setState({ showCancelScheduleConfirmationModal: false })}
          onCancelSchedule={this.onCancelSchedule} />
        <SchedulePublishModal
          enableModal={showSchedulePublishModal}
          onSchedulePublish={() => this.onPublish(true, true)}
          onClose={this.onCloseSchedulePublishModal}
          scheduledDateInputValue={scheduledDateInputValue}
          setDate={this.setScheduledDate}
        />
        <ArticlePreviewModal
            enableModal={showArticlePreviewModal}
            onChange={this.onChangeArticlePreview}
            onClose={this.onClosePreview}
            onSubmit={this.onSubmitArticlePreview}
            isPublic={previewSettingValue}
        />
      </div>
    )
  }
}

export default ArticleCreator
